import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Spinner } from "../components/ui/Spinner";
import { useApi } from "../context/ApiContext";
import { PageRenderer } from "../components/pageBuilder/PageRenderer";
import { IComponentNode } from "../types/pageBuilder.types";

export const PolicyPage: React.FC = () => {
 const { t } = useTranslation();
 const { slug } = useParams<{ slug: string }>();
 const { api } = useApi();
 const [page, setPage] = useState<any>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);

 useEffect(() => {
  if (slug) {
   fetchPage();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [slug]);

 const fetchPage = async () => {
  try {
   setLoading(true);
   setError(null);
   
   // Fetch from unified PageModel
   try {
    const response = await api.get(`/pages/${slug}`);
    if (response.data) {
     setPage(response.data);
     setLoading(false);
     return;
    }
   } catch (error: any) {
    // If not found, show error
    if (error.response?.status === 404) {
     setError(t("notFound.title"));
    } else {
     const errorMessage = error.response?.data?.error || error.message || t("common.error");
     setError(errorMessage);
    }
   }
  } catch (error: any) {
   console.error("Error fetching page:", error);
   const errorMessage = error.response?.data?.error || error.message || t("common.error");
   setError(errorMessage);
  } finally {
   setLoading(false);
  }
 };

 if (loading) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 if (error || !page) {
  return (
   <Container>
    <Stack align="center" spacing="md">
     <Heading size="xl">{error || t("notFound.title")}</Heading>
     <p className="text-gray-500">{t("notFound.pageNotFoundMessage")}</p>
    </Stack>
   </Container>
  );
 }

 // If page has zones, render zones
 if (page.zones && Object.keys(page.zones).length > 0) {
  const zoneNames = Object.keys(page.zones);
  return (
   <Container>
    <Stack spacing="none">
     {zoneNames.map((zoneName) => {
      const zoneContent = page.zones[zoneName];
      if (!zoneContent) return null;
      return (
       <div key={zoneName}>
        <PageRenderer layout={zoneContent as IComponentNode} />
       </div>
      );
     })}
    </Stack>
   </Container>
  );
 }

 // If page has layout, render layout
 const hasPageBuilderLayout = page.layout && typeof page.layout === "object" && page.layout.id;
 if (hasPageBuilderLayout) {
  return (
   <Container>
    <Stack spacing="lg">
     <PageRenderer layout={page.layout as IComponentNode} />
    </Stack>
   </Container>
  );
 }

 // Fallback to content (for pages with raw HTML content)
 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">{page.title}</Heading>
    <div
     className="prose max-w-none"
     dangerouslySetInnerHTML={{ __html: page.content || "" }}
    />
   </Stack>
  </Container>
 );
};

export default PolicyPage;

